<template>
    <page-layout :readonly="isProposalAccepted" :hide-footer="isProposalAccepted">
        <template #header>
            <proposal-step-header
                :current-step="stepNumber"
                :total-steps="totalSteps"
                :use-exit-modal="false"
                :title="tasksTitle"
                :is-loading="isLoading"
            />
        </template>
        <template #default>
            <g-loader v-if="isLoading" type="fixed" />
            <template v-else>
                <div class="margin-bottom-normal">
                    <g-typography tag="p" :value="tasksDescription" class="break-new-line" />
                </div>

                <error-message-card v-if="errorMessage" class="margin-bottom-normal" :message="errorMessage" />
                <proposal-step-error
                    v-if="proposalStep && proposalStep.error"
                    class="margin-bottom-normal"
                    :step="proposalStep"
                />
                <float-on-mask tag="div" :float-up="isProposalAccepted">
                    <tags-box :tags="tags" :custom-description="tagsDescription" />
                </float-on-mask>
                <template v-if="tasks.length > 0">
                    <div v-if="!isProposalAccepted" class="margin-bottom-normal">
                        <g-typography tag="h5" font-weight="semibold" value="Your Tasks" />
                    </div>
                    <div v-for="task in tasks" :key="task.id">
                        <float-on-mask tag="div" :float-up="isProposalAccepted">
                            <g-collapsible-card
                                :active="true"
                                :title="getTaskTitle(task)"
                                class="no-margins collapsible-card"
                            >
                                <template #extra>
                                    <g-button
                                        v-if="isDelivered(task) && isInstagramStoryTask(task) && !hasInsights(task)"
                                        theme="live"
                                        shape="round"
                                        @click="goToDeliveryPage(task)"
                                    >
                                        <g-icon name="upload" />
                                        <g-typography size="small">Upload Story Insights</g-typography>
                                    </g-button>

                                    <g-button
                                        v-if="isProposalAccepted && hasNetwork(task) && !isDelivered(task)"
                                        shape="circle"
                                        icon="upload"
                                        theme="live"
                                        @click="goToDeliveryPage(task)"
                                    />
                                    <g-button
                                        v-else-if="!task.required && !isProposalAccepted"
                                        shape="round"
                                        theme="live"
                                        @click="removeTask(task)"
                                    >
                                        Remove
                                    </g-button>
                                </template>
                                <template #footer>
                                    <g-media-object>
                                        <template #left>
                                            <network-icon :network="getNetworkSlug(task)" />
                                        </template>
                                        <template>
                                            <g-typography tag="span" size="small" :value="getNetworkName(task)" />
                                            <g-typography
                                                v-if="isCreatorRequired(task)"
                                                tag="span"
                                                size="small"
                                                color="error"
                                                value=" (Creator/Business Account Required)"
                                            />
                                        </template>
                                        <template v-if="isProposalAccepted && isDelivered(task)" #right>
                                            <div class="task-delivered">
                                                <g-icon name="check-circle" type="solid" />
                                                <g-typography value="Delivered" color="none" class="task-delivered" />
                                            </div>
                                        </template>
                                    </g-media-object>
                                </template>
                                <div class="padding-top-normal" v-html="$sanitize(task.requirements)" />
                            </g-collapsible-card>
                        </float-on-mask>
                    </div>
                </template>
            </template>
            <template v-if="!isProposalAccepted && optionalTasks(true).length > 0">
                <div class="margin-bottom-normal">
                    <g-typography tag="h5" font-weight="semibold" value="Optional Tasks" />
                </div>
                <div v-for="task in optionalTasks(true)" :key="task.id">
                    <g-collapsible-card :title="getTaskTitle(task)" class="no-margins collapsible-card">
                        <template #extra>
                            <g-button shape="round" theme="live" @click="addTask(task)">Add Task</g-button>
                        </template>
                        <template #footer>
                            <g-media-object>
                                <template #left>
                                    <network-icon v-if="getNetworkSlug(task)" :network="getNetworkSlug(task)" />
                                </template>
                                <template #default>
                                    <g-typography tag="span" size="small" :value="getNetworkName(task)" />
                                    <g-typography
                                        v-if="isCreatorRequired(task)"
                                        tag="span"
                                        size="small"
                                        color="error"
                                        value=" (Creator/Business Account Required)"
                                    />
                                </template>
                            </g-media-object>
                        </template>
                        <div class="padding-top-normal" v-html="$sanitize(task.requirements)" />
                    </g-collapsible-card>
                </div>
            </template>
        </template>
        <template #footer>
            <proposal-step-footer
                :is-next-disabled="isDisabled"
                :loading="isCompletingProposal"
                :use-exit-modal="false"
                :step-completed="isCompleted"
                :current-step="stepNumber"
                :total-steps="totalSteps"
                @go-next-step="$emit('go-next-step', proposalStep, { task_ids: addedTasks })"
                @go-previous-step="$emit('go-previous-step', proposalStep)"
            />
        </template>
    </page-layout>
</template>

<script>
    import * as _ from 'lodash';
    import PageLayout from '../../layouts/PageLayout.vue';
    import ProposalStepHeader from './partials/ProposalStepHeader.vue';
    import ProposalStepFooter from './partials/ProposalStepFooter.vue';
    import ErrorMessageCard from './partials/ErrorMessageCard.vue';
    import ProposalStepError from './partials/PoposalStepError.vue';
    import FloatOnMask from '../../library/FloatOnMask.vue';
    import TagsBox from './partials/TagsBox.vue';
    import NetworkIcon from '../../../../src/components/interface/elements/NetworkIcon.vue';
    import TasksMixin from './mixins/TasksMixin';
    import EventTrackingMixin from '../../../mixins/EventTrackingMixin';
    import ProposalMixin from './mixins/ProposalMixin';
    import { NAME_TASKS } from '../../../constants/proposal-steps';
    import { LIVE_CAMPAIGN_PROPOSAL_OPTIONAL_TASK_ADDED_EVENT } from '../../../constants/analytics-events';
    import useNetworks from '../../../../src/composables/use-networks';

    export default {
        name: 'ProposalTasksView',
        components: {
            FloatOnMask,
            ProposalStepError,
            ErrorMessageCard,
            PageLayout,
            ProposalStepHeader,
            ProposalStepFooter,
            TagsBox,
            NetworkIcon
        },
        mixins: [TasksMixin, ProposalMixin, EventTrackingMixin],
        props: {
            campaign: {
                type: Object
            },
            proposal: {
                type: Object
            },
            proposalStep: {
                type: Object
            },
            isLoading: {
                type: Boolean,
                default: false
            },
            isCompletingProposal: {
                type: Boolean,
                default: false
            },
            errors: {
                type: Object,
                required: false,
                default: () => {}
            },
            errorMessage: {
                type: String,
                required: false
            }
        },
        computed: {
            tasksTitle() {
                const title = _.get(this, 'campaign.customization.tasks_step_title');

                return title ? title : 'Tasks';
            },
            tasksDescription() {
                const description = _.get(this, 'campaign.customization.tasks_step_overview');

                if (description !== null) {
                    return description;
                }

                return "These are the tasks you're required to complete as part of this campaign.";
            },
            hasCustomTagsDescription() {
                return _.get(this, 'campaign.customization.tracking_step_overview', null) !== null;
            },
            isDisabled() {
                return false;
            },
            isCompleted() {
                return _.get(this, 'proposalStep.is_complete', false);
            },
            stepNumber() {
                return _.get(this, 'proposalStep.meta.stepNumber', 0);
            },
            totalSteps() {
                return _.get(this, 'proposalStep.meta.totalSteps', 0);
            },
            jobs() {
                return _.get(this, 'proposal.jobs', []);
            },
            tasks() {
                return this.isProposalAccepted ? this.proposalTasks : this.requiredTasks(true);
            }
        },
        methods: {
            getNetworkSlug(task) {
                if (this.hasNetwork(task)) {
                    return task.network.slug;
                }

                return '';
            },
            getNetworkName(task) {
                if (this.hasNetwork(task)) {
                    return task.network.name;
                }

                return 'Other';
            },
            getNetworkIconType(task) {
                if (this.hasNetwork(task)) {
                    return 'brand';
                }

                return 'light';
            },
            isCreatorRequired(task) {
                return useNetworks().isInstagramGraphRequiredForCampaignTask(task);
            },
            isInstagramStoryTask(task) {
                return this.getNetworkSlug(task) === 'instagram' && task.media_type === 'story';
            },
            addTask(task) {
                this.$emit('patch-step', {
                    step: NAME_TASKS,
                    complete: false,
                    payload: { task_ids: [...this.addedTasks, task.id] }
                });
                this.$bus.$emit(LIVE_CAMPAIGN_PROPOSAL_OPTIONAL_TASK_ADDED_EVENT);
                this.trackEventOptionalTaskAdded(this.campaign.id);
            },
            removeTask(task) {
                const tasks = this.addedTasks.filter((item) => item !== task.id);
                this.$emit('patch-step', {
                    step: NAME_TASKS,
                    complete: false,
                    payload: { task_ids: tasks }
                });
            },
            getJob(task) {
                return this.jobs.find((job) => job.task_id === task.id);
            },
            isDelivered(task) {
                const job = this.getJob(task);

                return job ? job.delivered_at !== null : false;
            },
            hasInsights(task) {
                const job = this.getJob(task);

                return job ? !!job.insights_file_id : false;
            }
        }
    };
</script>

<style lang="scss" scoped>
    .margin-bottom-small {
        margin-bottom: $g-spacing-3;
    }

    .margin-bottom-normal {
        margin-bottom: $g-spacing-5;
    }

    .margin-bottom-large {
        margin-bottom: $g-spacing-6;
    }

    .no-margins {
        & ::v-deep p:last-child {
            margin: 0;
        }
    }

    .collapsible-card {
        margin-bottom: $g-spacing-5;
    }

    .collapsible-card:last-child {
        margin-bottom: $g-spacing-6;
    }

    .padding-top-normal {
        padding-top: $g-spacing-5;
    }

    .task-delivered {
        color: $g-color-green-5;
    }

    .break-new-line {
        white-space: pre-line;
    }
    .gi-tiktok {
        color: #000;
        width: 1.25em;
        display: inline-block;
        text-align: center;
        margin-right: 1em !important;
        height: 1.5rem;
    }
</style>
